import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Stack, IconButton, Tooltip, Checkbox, FormControlLabel, FormGroup,
  MenuItem, Switch, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import WebhookIcon from '@mui/icons-material/Webhook';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendIcon from '@mui/icons-material/Send';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import Swal from 'sweetalert2';
import request from '../../../services/Request';
import ModernTable, { ModernPageHeader } from '../components/ModernTable';

const BASE = '/external/admin';

const ALL_EVENTS = [
  { value: 'member.registered', label: 'Member Registered' },
  { value: 'member.purchase', label: 'Member Purchase' },
  { value: 'order.completed', label: 'Order Completed' },
  { value: 'machine.restocked', label: 'Machine Restocked' },
  { value: 'machine.planogram_updated', label: 'Planogram Updated' },
];
const DEFAULT_EVENTS = ['member.registered', 'member.purchase', 'order.completed'];

const formatTime = (t) => (t ? new Date(t).toLocaleString() : '');

const showSendResult = (result) =>
  Swal.fire(
    result.ok
      ? { icon: 'success', title: 'Delivered', text: `Endpoint responded ${result.statusCode}` }
      : {
          icon: 'error',
          title: 'Delivery failed',
          text: result.statusCode ? `Endpoint responded ${result.statusCode}: ${result.error}` : result.error,
        }
  );

export default function Webhooks() {
  const theme = useTheme();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const reload = () => setRefresh((r) => !r);

  // Register / Edit dialog. editingId 为空表示新建
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState(DEFAULT_EVENTS);
  const [active, setActive] = useState(true);

  // Test dialog
  const [testTarget, setTestTarget] = useState(null);
  const [testEvent, setTestEvent] = useState('');
  const [testing, setTesting] = useState(false);

  // Failures dialog
  const [failuresTarget, setFailuresTarget] = useState(null);
  const [failures, setFailures] = useState([]);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request({ method: 'get', url: `${BASE}/webhooks` }).then((res) => {
      if (!cancelled) { setWebhooks(res.data); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [refresh]);

  const toggleEvent = (value) =>
    setEvents((prev) => prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]);

  const openCreate = () => {
    setEditingId(null);
    setUrl('');
    setEvents(DEFAULT_EVENTS);
    setActive(true);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setUrl(row.url);
    setEvents(row.events || []);
    setActive(row.active);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!url.trim() || events.length === 0) return;
    try {
      if (editingId) {
        await request({ method: 'put', url: `${BASE}/webhooks/${editingId}`, data: { url, events, active } });
      } else {
        await request({ method: 'post', url: `${BASE}/webhooks`, data: { url, events } });
      }
      Swal.fire({ icon: 'success', text: editingId ? 'Webhook updated' : 'Webhook registered', timer: 1500, showConfirmButton: false });
      setOpen(false);
      reload();
    } catch {
      Swal.fire({ icon: 'error', text: editingId ? 'Failed to update webhook' : 'Failed to register webhook' });
    }
  };

  const handleDelete = async (id, webhookUrl) => {
    const confirm = await Swal.fire({
      title: 'Delete webhook?', text: webhookUrl, icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete',
      confirmButtonColor: theme.palette.error.main,
    });
    if (!confirm.isConfirmed) return;
    await request({ method: 'delete', url: `${BASE}/webhooks/${id}` });
    Swal.fire({ icon: 'success', text: 'Webhook deleted', timer: 1200, showConfirmButton: false });
    reload();
  };

  const openTest = (row) => {
    setTestTarget(row);
    setTestEvent((row.events || [])[0] || ALL_EVENTS[0].value);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await request({ method: 'post', url: `${BASE}/webhooks/${testTarget._id}/test`, data: { event: testEvent } });
      setTestTarget(null);
      showSendResult(res.data);
    } catch {
      Swal.fire({ icon: 'error', text: 'Failed to send test event' });
    } finally {
      setTesting(false);
    }
  };

  const loadFailures = async (webhookId) => {
    const res = await request({ method: 'get', url: `${BASE}/webhooks/${webhookId}/failures` });
    setFailures(res.data);
  };

  const openFailures = (row) => {
    setFailuresTarget(row);
    setFailures([]);
    loadFailures(row._id);
  };

  const handleResend = async (failureId) => {
    setResendingId(failureId);
    try {
      const res = await request({ method: 'post', url: `${BASE}/webhooks/failures/${failureId}/resend` });
      showSendResult(res.data);
      loadFailures(failuresTarget._id);
      reload();
    } catch {
      Swal.fire({ icon: 'error', text: 'Failed to resend' });
    } finally {
      setResendingId(null);
    }
  };

  const renderLastDelivery = (row) => {
    const success = row.lastSuccessAt ? new Date(row.lastSuccessAt) : null;
    const failure = row.lastFailureAt ? new Date(row.lastFailureAt) : null;
    if (!success && !failure) {
      return <Typography variant="body2" color="text.secondary">No deliveries yet</Typography>;
    }
    const ok = success && (!failure || success > failure);
    return (
      <Tooltip title={ok ? '' : row.lastError || ''}>
        <Box>
          <Chip
            label={ok ? 'OK' : `Failed${row.lastStatusCode ? ` (${row.lastStatusCode})` : ''}`} size="small"
            sx={{ bgcolor: ok ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                  color: ok ? theme.palette.success.dark : theme.palette.error.dark }}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {formatTime(ok ? success : failure)}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'URL', field: 'url',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: theme.palette.primary.main }}>
          {row.url}
        </Typography>
      ),
    },
    {
      title: 'Events', field: 'events',
      render: (row) => (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {(row.events || []).map((e) => (
            <Chip key={e} label={e} size="small" sx={{ fontSize: 10, height: 20, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
          ))}
        </Stack>
      ),
    },
    {
      title: 'Status', field: 'active',
      render: (row) => (
        <Chip
          label={row.active ? 'Active' : 'Inactive'} size="small"
          sx={{ bgcolor: row.active ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                color: row.active ? theme.palette.success.dark : theme.palette.error.dark }}
        />
      ),
    },
    { title: 'Last Delivery', field: 'lastSuccessAt', render: renderLastDelivery },
    {
      title: 'Created', field: 'createdAt',
      render: (row) => <Typography variant="body2" color="text.secondary">{new Date(row.createdAt).toLocaleDateString()}</Typography>,
    },
    {
      title: 'Action', field: '_id',
      render: (row) => (
        <Stack direction="row">
          <Tooltip title="Send test event">
            <IconButton size="small" onClick={() => openTest(row)} sx={{ color: theme.palette.primary.main }}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Failed deliveries">
            <IconButton size="small" onClick={() => openFailures(row)} sx={{ color: theme.palette.warning.main }}>
              <ErrorOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit webhook">
            <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: theme.palette.text.secondary }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete webhook">
            <IconButton size="small" onClick={() => handleDelete(row._id, row.url)}
              sx={{ color: theme.palette.error.main, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <ModernPageHeader
        title="Webhooks"
        subtitle="Push real-time events to external systems when members register or purchase, or machines are restocked"
        icon={WebhookIcon}
        buttonText="Register Webhook"
        buttonIcon={AddIcon}
        onButtonClick={openCreate}
      />

      <ModernTable
        isLoading={loading}
        columns={columns}
        data={webhooks}
        options={{ actionsColumnIndex: -1, pageSize: 10, search: false }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? 'Edit Webhook' : 'Register Webhook'}</DialogTitle>
        <DialogContent>
          <TextField
            label="URL" value={url} onChange={(e) => setUrl(e.target.value)}
            fullWidth variant="outlined" placeholder="https://your-server.com/webhook"
            helperText={
              editingId
                ? 'Leave **** as is to keep the current password.'
                : 'For Basic Auth, put the credentials in the URL: https://user:password@host/path'
            }
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Events</Typography>
          <FormGroup>
            {ALL_EVENTS.map((ev) => (
              <FormControlLabel
                key={ev.value}
                control={<Checkbox checked={events.includes(ev.value)} onChange={() => toggleEvent(ev.value)} size="small" />}
                label={<Typography variant="body2">{ev.label}</Typography>}
              />
            ))}
          </FormGroup>
          {editingId && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label={<Typography variant="body2">Active</Typography>}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!url.trim() || events.length === 0}>
            {editingId ? 'Save' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!testTarget} onClose={() => setTestTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Send Test Event</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends sample data marked with "test": true. Nothing is saved.
          </Typography>
          <TextField
            select label="Event" value={testEvent} onChange={(e) => setTestEvent(e.target.value)}
            fullWidth variant="outlined"
          >
            {ALL_EVENTS.map((ev) => (
              <MenuItem key={ev.value} value={ev.value}>{ev.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTestTarget(null)}>Cancel</Button>
          <Button onClick={handleTest} variant="contained" disabled={testing}>
            {testing ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!failuresTarget} onClose={() => setFailuresTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Failed Deliveries</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Events that still failed after 3 attempts, kept for 30 days. Resend uses the current URL.
          </Typography>
          {failures.length === 0 ? (
            <Typography variant="body2">No failed deliveries.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell>Attempts</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {failures.map((f) => (
                  <TableRow key={f._id}>
                    <TableCell>{formatTime(f.createdAt)}</TableCell>
                    <TableCell>{f.event}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{f.error}</TableCell>
                    <TableCell>{f.attempts}</TableCell>
                    <TableCell>
                      <Button
                        size="small" startIcon={<ReplayIcon />}
                        disabled={resendingId === f._id}
                        onClick={() => handleResend(f._id)}
                      >
                        Resend
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFailuresTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
